import React, { useState, useEffect } from 'react';
import './VisualUniverseDialog.css';
import dayjs from 'dayjs'; // ADDED: for default date values

// --- BEGIN SIGN TEXT LINE COORDINATES ---
// Each subarray: [ {x, y}, ... ] for sign 0 (Aries) to sign 11 (Pisces)
export const SIGN_LINE_COORDS = [
  [ {x:930, y:265}, {x:1030, y:305}, {x:1030, y:225}, {x:1130, y:345}, {x:1130, y:185} ], // Aries
  [ {x:205, y:40}, {x:290, y:75}, {x:390, y:110}, {x:490, y:145} ], // Taurus
  [ {x:20, y:265}, {x:20, y:305}, {x:20, y:345}, {x:20, y:225}, {x:20, y:180} ], // Gemini
  [ {x:200, y:510}, {x:300, y:550}, {x:300, y:470}, {x:400, y:590}, {x:400, y:430} ], // Cancer
  [ {x:20, y:755}, {x:20, y:795}, {x:20, y:835}, {x:20, y:715}, {x:20, y:670} ], // Leo
  [ {x:180, y:980}, {x:265, y:945}, {x:365, y:910}, {x:465, y:875} ], // Virgo
  [ {x:930, y:760}, {x:1030, y:800}, {x:1030, y:720}, {x:1130, y:840}, {x:1130, y:680} ], // Libra
  [ {x:1620, y:980}, {x:1705, y:945}, {x:1790, y:910}, {x:1875, y:875} ], // Scorpio
  [ {x:2300, y:755}, {x:2385, y:795}, {x:2470, y:835}, {x:2385, y:715}, {x:2470, y:670} ], // Sagittarius
  [ {x:1710, y:460}, {x:1650, y:500}, {x:1590, y:540}, {x:1530, y:580}, {x:1470, y:620} ], // Capricorn
  [ {x:2300, y:265}, {x:2385, y:305}, {x:2470, y:345}, {x:2385, y:225}, {x:2470, y:180} ], // Aquarius
  [ {x:1645, y:40}, {x:1730, y:75}, {x:1830, y:110}, {x:1930, y:140} ] // Pisces
];
// --- END SIGN TEXT LINE COORDINATES ---

import PropTypes from 'prop-types';
import { Dialog, DialogTitle, DialogContent, Button, Box, Typography, TextField, Select, MenuItem, FormControl, FormControlLabel, Checkbox } from '@mui/material';
import HelpIcon from './HelpIcon';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Tooltip from '@mui/material/Tooltip';



// --- ChartSVG: Top-level component for chart rendering ---
// --- BEGIN TEMPORARY COORDINATE CAPTURE ---
const ChartSVG = ({ showBackground = true, signTextLines = null, signTooltips = [] }) => {
    const width = 2916;
    const height = 1026;
    const topLeft = { x: 0, y: 0 };
    const topRight = { x: width, y: 0 };
    const bottomLeft = { x: 0, y: height };
    const bottomRight = { x: width, y: height };
    const topCenter = { x: width / 2, y: 0 };
    const rightCenter = { x: width, y: height / 2 };
    const bottomCenter = { x: width / 2, y: height };
    const leftCenter = { x: 0, y: height / 2 };
    const center = { x: width / 2, y: height / 2 };
    // Define sign positions
    const signPositions = [
      { sign: 'ARIES',       x: width * 0.45, y: height * 0.25 },
      { sign: 'TAURUS',      x: width * 0.20, y: height * 0.10 },
      { sign: 'GEMINI',      x: width * 0.05, y: height * 0.25 },
      { sign: 'CANCER',      x: width * 0.20, y: height * 0.50 },
      { sign: 'LEO',         x: width * 0.05, y: height * 0.75 },
      { sign: 'VIRGO',       x: width * 0.20, y: height * 0.90 },
      { sign: 'LIBRA',       x: width * 0.45, y: height * 0.75 },
      { sign: 'SCORPIO',     x: width * 0.70, y: height * 0.90 },
      { sign: 'SAGITTARIUS', x: width * 0.85 - 1.5 * 72, y: height * 0.75 },
      { sign: 'CAPRICORN',   x: width * 0.65, y: height * 0.50 },
      { sign: 'AQUARIUS',    x: width * 0.85, y: height * 0.25 },
      { sign: 'PISCES',      x: width * 0.70, y: height * 0.10 },
    ];

    /**
     * Utility to draw a labeled line between two signs on the SVG chart, supporting multiple parallel lines.
     * @param {string} signA - Name of the first sign (e.g. 'ARIES')
     * @param {string} signB - Name of the second sign (e.g. 'LIBRA')
     * @param {string} label - Text to display near the line
     * @param {string} color - Stroke color for the line
     * @param {number} thickness - Stroke width for the line
     * @param {number} [index=0] - Index of this line among all lines between the same two signs
     * @param {number} [total=1] - Total number of lines between the same two signs
     * @returns SVG group element with line and label
     *
     * To avoid overlap, pass the index and total for each line between the same sign pair.
     */

    // Usage: for multiple lines, call with index and total, e.g.:
    // drawLabeledSignLine('ARIES', 'LIBRA', 'Label1', '#4caf50', 5, 0, 3)
    // drawLabeledSignLine('ARIES', 'LIBRA', 'Label2', '#e91e63', 5, 1, 3)
    // drawLabeledSignLine('ARIES', 'LIBRA', 'Label3', '#2196f3', 5, 2, 3)

    const writeText = (x, y, text, isSignName = false) => (
      <text
        key={`text-${x}-${y}-${text}`}
        x={width * x}
        y={height * y}
        textAnchor="start"
        dominantBaseline="hanging"
        style={{
          fontSize: isSignName ? '72px' : '20px',
          fontWeight: isSignName ? 'bold' : 'normal',
          fill: isSignName ? '#e2f1ff' : '#555555',
          fontFamily: 'Arial, sans-serif'
        }}
      >
        {text}
      </text>
    );
    // --- BEGIN SIGN TEXT LINE RENDERING ---
    // Helper: Parse text with <t>n</t>, <t>s</t>, <t>f</t> for color and <b></b> for bold, supporting nesting
    const parseStyledText = (input) => {
      // Recursive parser for nested tags
      const TAG_REGEX = /<(t|b)(?:>([nsf])|)>|<\/(t|b)>/gi;
      const stack = [{ color: 'black', bold: false }];
      const colorMap = { n: 'black', s: 'red', f: 'blue' };

      let result = [];
      let lastIdx = 0;
      let match;
      let curr = stack[stack.length - 1];
      while ((match = TAG_REGEX.exec(input))) {
        if (match.index > lastIdx) {
          // Push text between tags
          const text = input.slice(lastIdx, match.index);
          if (text) result.push({ text, ...curr });
        }
        if (match[1]) {
          // Opening tag
          if (match[1] === 't') {
            // Color tag
            stack.push({ ...curr, color: colorMap[match[2]] || 'black' });
          } else if (match[1] === 'b') {
            stack.push({ ...curr, bold: true });
          }
          curr = stack[stack.length - 1];
        } else if (match[3]) {
          // Closing tag
          stack.pop();
          curr = stack[stack.length - 1];
        }
        lastIdx = TAG_REGEX.lastIndex;
      }
      if (lastIdx < input.length) {
        result.push({ text: input.slice(lastIdx), ...curr });
      }
      return result;
    };

    // Render sign text lines with color and bold, and tooltip on hover

    const renderSignTextLines = () => {
      if (!signTextLines) return null;
      const elements = [];
      for (let signIdx = 0; signIdx < SIGN_LINE_COORDS.length; ++signIdx) {
        const orderedCoords = [...SIGN_LINE_COORDS[signIdx]].sort((a, b) => {
          if (a.y !== b.y) return a.y - b.y;
          return a.x - b.x;
        });
        // Place a dot for the first line position of each sign
        const { x, y } = orderedCoords[0];
        // Estimate end of first line for dot placement
        const firstLine = signTextLines[signIdx][0] || '';
        // Estimate width: 9px per char, fallback to 120px if empty
        const textLength = firstLine.replace(/<[^>]+>/g, '').length;
        const estWidth = textLength > 0 ? textLength * 9 : 120;
        const dotX = x + estWidth + 12; // 12px padding after text
        // Dot with tooltip (larger)
        elements.push(
          <foreignObject
            key={`signdot-${signIdx}`}
            x={dotX - 16}
            y={y - 12}
            width={40}
            height={40}
            style={{ overflow: 'visible' }}
          >
            <Tooltip
              title={<span style={{ whiteSpace: 'pre-line' }}>{signTooltips[signIdx]}</span>}
              placement="top"
              arrow
            >
              <svg width={32} height={32} style={{ pointerEvents: 'all', cursor: 'pointer' }}>
                <circle cx={16} cy={16} r={10} fill="#8e44ad" stroke="#fff" strokeWidth={2} />
              </svg>
            </Tooltip>
          </foreignObject>
        );
        // Render the sign text lines as before
        for (let lineIdx = 0; lineIdx < orderedCoords.length; ++lineIdx) {
          const text = signTextLines[signIdx][lineIdx];
          if (text && typeof text === 'string' && text.trim()) {
            const { x, y } = orderedCoords[lineIdx];
            const styledSegments = parseStyledText(text);
            elements.push(
              <text
                key={`signline-${signIdx}-${lineIdx}`}
                x={x}
                y={y}
                fontSize="16"
                fontFamily="Arial, sans-serif"
              >
                {styledSegments.map((seg, i) => (
                  <tspan
                    key={i}
                    fill={seg.color}
                    fontWeight={seg.bold ? 'bold' : 'bold'}
                    className={seg.color === 'blue' ? 'flash' : undefined}
                  >
                    {seg.text}
                  </tspan>
                ))}
              </text>
            );
          }
        }
      }
      return elements;
    };

    // --- END SIGN TEXT LINE RENDERING ---


    
    const houses = [
      { path: `M ${topCenter.x},${topCenter.y} L ${rightCenter.x},${rightCenter.y} L ${center.x},${center.y} Z`, fill: '#f0f8ff' },
      { path: `M ${topLeft.x},${topLeft.y} L ${topCenter.x},${topCenter.y} L ${center.x},${center.y} Z`, fill: '#e6e6fa' },
      { path: `M ${topLeft.x},${topLeft.y} L ${leftCenter.x},${leftCenter.y} L ${center.x},${center.y} Z`, fill: '#e0ffff' },
      { path: `M ${leftCenter.x},${leftCenter.y} L ${bottomLeft.x},${bottomLeft.y} L ${center.x},${center.y} Z`, fill: '#f0fff0' },
      { path: `M ${bottomLeft.x},${bottomLeft.y} L ${bottomCenter.x},${bottomCenter.y} L ${center.x},${center.y} Z`, fill: '#fff0f5' },
      { path: `M ${bottomCenter.x},${bottomCenter.y} L ${bottomRight.x},${bottomRight.y} L ${center.x},${center.y} Z`, fill: '#f5fffa' },
      { path: `M ${bottomRight.x},${bottomRight.y} L ${rightCenter.x},${rightCenter.y} L ${center.x},${center.y} Z`, fill: '#f0f0ff' },
      { path: `M ${rightCenter.x},${rightCenter.y} L ${topRight.x},${topRight.y} L ${center.x},${center.y} Z`, fill: '#fffaf0' },
      { path: `M ${topRight.x},${topRight.y} L ${topCenter.x},${topCenter.y} L ${center.x},${center.y} Z`, fill: '#f5f5f5' },
      { path: `M ${topCenter.x},${topCenter.y} L ${rightCenter.x},${rightCenter.y} L ${bottomCenter.x},${bottomCenter.y} L ${leftCenter.x},${leftCenter.y} Z`, fill: '#f0ffff' },
      { path: `M ${rightCenter.x},${rightCenter.y} L ${bottomRight.x},${bottomRight.y} L ${center.x},${center.y} Z`, fill: '#fff5ee' },
      { path: `M ${topRight.x},${topRight.y} L ${rightCenter.x},${rightCenter.y} L ${center.x},${center.y} Z`, fill: '#f0ffe0' },
    ];
    return (
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ width: '100%', height: '100%' }}
        // --- BEGIN TEMPORARY COORDINATE CAPTURE ---
        // onClick={e => {
        //   if (typeof onChartClick === 'function' && e.button === 0) {
        //     // Only handle left mouse button
        //     const svg = e.currentTarget;
        //     const rect = svg.getBoundingClientRect();
        //     // Calculate SVG-relative coordinates
        //     const x = Math.round((e.clientX - rect.left) * (width / rect.width));
        //     const y = Math.round((e.clientY - rect.top) * (height / rect.height));
        //     // Only accept clicks inside SVG bounds
        //     if (x >= 0 && x <= width && y >= 0 && y <= height) {
        //       onChartClick({ x, y });
        //     }
        //   }
        // }}
        // --- END TEMPORARY COORDINATE CAPTURE ---
      >
        {showBackground && houses.map((house, index) => (
          <path 
            key={`house-${index}`} 
            d={house.path} 
            fill={house.fill} 
            stroke="none"
          />
        ))}
        <rect x="0" y="0" width={width} height={height} fill="none" stroke="black" strokeWidth="2" />
        <line x1="0" y1="0" x2="0" y2={height} stroke="black" strokeWidth="4" />
        <line x1={width} y1="0" x2={width} y2={height} stroke="black" strokeWidth="4" />
        <line x1={topLeft.x} y1={topLeft.y} x2={bottomRight.x} y2={bottomRight.y} stroke="black" strokeWidth="2" />
        <line x1={topRight.x} y1={topRight.y} x2={bottomLeft.x} y2={bottomLeft.y} stroke="black" strokeWidth="2" />
        <line x1={topCenter.x} y1={topCenter.y} x2={rightCenter.x} y2={rightCenter.y} stroke="black" strokeWidth="2" />
        <line x1={rightCenter.x} y1={rightCenter.y} x2={bottomCenter.x} y2={bottomCenter.y} stroke="black" strokeWidth="2" />
        <line x1={bottomCenter.x} y1={bottomCenter.y} x2={leftCenter.x} y2={leftCenter.y} stroke="black" strokeWidth="2" />
        <line x1={leftCenter.x} y1={leftCenter.y} x2={topCenter.x} y2={topCenter.y} stroke="black" strokeWidth="2" />
        <g>
          {signPositions.map((item) => (
        <g key={item.sign}>
          {writeText(item.x / width, item.y / height, item.sign, true)}
          {/* No sample lines rendered; backend integration will populate these dynamically */}
        </g>
      ))}
        </g>
      {/* --- BEGIN SIGN TEXT LINES --- */}
      {renderSignTextLines()}
      </svg>
    );
};

ChartSVG.propTypes = {
  showBackground: PropTypes.bool.isRequired,
  horoscopeData: PropTypes.array,
  signTextLines: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)),
  signTooltips: PropTypes.arrayOf(PropTypes.string),
};

import { useAuth } from '../context/AuthContext';

const VisualUniverseDialog = ({ open, onClose, initialDate }) => {
  const { api } = useAuth();
  // --- BEGIN TEMPORARY COORDINATE CAPTURE ---
// Handler for SVG click events
// const handleChartClick = ({ x, y }) => {
//   setStatusText(`Clicked at (${x}, ${y})`);
// };
// --- END TEMPORARY COORDINATE CAPTURE ---
  const [statusText, setStatusText] = useState('');
  const [showConfig, setShowConfig] = useState(false);
  const [showBackground, setShowBackground] = useState(true);
  const [rate, setRate] = useState(1);
  // Default change unit: 24 hours (instead of 2)
  const [unit, setUnit] = useState(24); // CHANGED: default is now 24 hours
  // Load dates from localStorage or use defaults
  const [fromDate, setFromDate] = useState(null);

  useEffect(() => {
    if (initialDate) {
      setFromDate(dayjs(initialDate));
    } else {
      const saved = localStorage.getItem('visualUniverse_fromDate');
      setFromDate(saved ? dayjs(saved) : dayjs());
    }
  }, [initialDate, open]);

  const [toDate, setToDate] = useState(() => {
    const saved = localStorage.getItem('visualUniverse_toDate');
    return saved ? dayjs(saved) : dayjs('2026-12-31');
  });
  // Previous:
  // const [fromDate, setFromDate] = useState('2025-01-01');
  // const [toDate, setToDate] = useState('2026-12-31');
  // Animation phase: 'idle' | 'running' | 'paused'
  const [animationPhase, setAnimationPhase] = useState('idle');
  const [signTextLines, setSignTextLines] = useState(() => Array(12).fill([]));
  const [signTooltips, setSignTooltips] = useState(() => Array(12).fill(''));

  // Track the chart date currently being displayed
  const [currentChartDate, setCurrentChartDate] = useState(null);
  // Animation direction: 1 = forward, -1 = reverse
  const [animationDirection, setAnimationDirection] = useState(1);


  const timerRef = React.useRef(null);
  const lastServiceTimeRef = React.useRef(null);

  const isStartEnabled = fromDate && toDate;

  // Auto-set direction to forward when start is enabled
  React.useEffect(() => {
    if (isStartEnabled) setAnimationDirection(1);
  }, [isStartEnabled]);

  // Keep currentChartDate in sync with fromDate and animation
  React.useEffect(() => {
    if (animationPhase === 'idle') {
      setCurrentChartDate(fromDate);
    }
  }, [fromDate, animationPhase]);


  // Helper: convert Dayjs or Date to epoch milliseconds
  const toEpochMillis = (dateObj) => {
    if (!dateObj) return null;
    if (typeof dateObj.valueOf === 'function') return dateObj.valueOf();
    return new Date(dateObj).getTime();
  };

  // Real backend call for horoscope (now sending currentDate and previousDate)
  const fetchHoroscope = async (currentDate) => {
    try {
      setStatusText('Loading horoscope...');
      const res = await api.post('/api/horoscope', { currentDate });
      const data = res.data;
      // Expecting: data = { lines: [...], tooltips: [...] }
      if (data && Array.isArray(data.lines) && Array.isArray(data.tooltips) && data.lines.length === 12 && data.tooltips.length === 12) {
        setStatusText('');
        setSignTextLines(data.lines);
        setSignTooltips(data.tooltips);
      } else {
        setStatusText('Unexpected data from backend');
        setSignTextLines(Array(12).fill([]));
        setSignTooltips(Array(12).fill(''));
      }
    } catch (e) {
      setStatusText('Error fetching horoscope');
      setSignTextLines(Array(12).fill([]));
      setSignTooltips(Array(12).fill(''));
    }
  };

  // Helper to format date as yyyy-MM-ddTHH:mm:ss
  const formatDateTime = (dateObj) => {
    if (!dateObj) return '';
    const d = dayjs(dateObj);
    return d.format('YYYY-MM-DDTHH:mm:ss');
  };




  // Timer logic
  React.useEffect(() => {
    if (animationPhase === 'running' && fromDate && toDate) {
      setCurrentChartDate(fromDate);
      const end = new Date(toDate);
      timerRef.current = setInterval(() => {
        const incrementMs = unit * 3600000;
        let nextTime = (lastServiceTimeRef.current ?? toEpochMillis(fromDate)) + animationDirection * incrementMs;
        if ((animationDirection === 1 && nextTime > end.getTime()) || (animationDirection === -1 && nextTime < new Date(fromDate).getTime())) {
          clearInterval(timerRef.current);
          setAnimationPhase('idle');
          setStatusText('Done');
          return;
        }
        // Compute previous time (previous bar)
        const prevTime = nextTime - animationDirection * incrementMs;
        fetchHoroscope(formatDateTime(new Date(nextTime)), formatDateTime(new Date(prevTime))).then(() => {
          lastServiceTimeRef.current = nextTime;
          setCurrentChartDate(new Date(nextTime));
        });
      }, rate * 1000);
      return () => clearInterval(timerRef.current);
    } else {
      clearInterval(timerRef.current);
    }
    // Deliberately omits fetchHoroscope and animationDirection. fetchHoroscope is a plain async
    // function declared in the component body, so it is a new reference on every render — listing
    // it would clear and recreate this interval each time, and an animation that restarts on every
    // render never advances. animationDirection is read when a tick fires, so a mid-run reversal
    // is already picked up without tearing the timer down and losing its phase.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animationPhase, fromDate, toDate, rate, unit]);

  // START button handler
  const handleStart = () => {
    setAnimationDirection(1); // ALWAYS start in forward direction
    setAnimationPhase('running');
    const startMillis = toEpochMillis(fromDate);
    lastServiceTimeRef.current = startMillis;
    setCurrentChartDate(fromDate);
    // For first call, previousDate = startMillis - unit * 3600000
    const prevMillis = startMillis - unit * 3600000;
    fetchHoroscope(formatDateTime(new Date(startMillis)), formatDateTime(new Date(prevMillis)));
  };


  // PAUSE/CONTINUE handler
  const handlePauseContinue = () => {
    if (animationPhase === 'running') {
      setAnimationPhase('paused');
    } else if (animationPhase === 'paused') {
      setAnimationPhase('running');
    }
  };




  // Cleanup: stop timer and reset state on CLOSE
  const handleClose = () => {
    clearInterval(timerRef.current);
    setAnimationPhase('idle');
    setStatusText('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          height: '95vh',
          width: 'calc((95vh - 175px) * 2916 / 1026)',
          maxWidth: '95vw',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 2 }}>
        <DialogTitle sx={{ m: 0, p: 0, fontSize: 28, lineHeight: 1.2, flex: 'none', minWidth: 0, display: 'flex', alignItems: 'center', gap: 2 }}>
          <span>Visual Universe</span>
          <HelpIcon helpKey="visualUniverseDialog" />
        </DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            control={<Checkbox checked={showConfig} onChange={() => setShowConfig(!showConfig)} disabled />}
            label="Show Config"
            sx={{ mr: 2 }}
          />
          <FormControlLabel
            control={<Checkbox checked={showBackground} onChange={() => setShowBackground(!showBackground)} />}
            label="Show Background"
            sx={{ ml: 0 }}
          />
        </Box>
      </Box>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', p: 0, overflow: 'hidden', height: '100%', position: 'relative' }}>
        {/* ConfigBox: empty container for future animation controls */}
        {showConfig && (
          <Box
            sx={{
              width: '100%',
              height: '50%',
              minHeight: 200,
              maxHeight: '50%',
              border: '2px dashed #8e44ad',
              background: '#faf7ff',
              borderRadius: 3,
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
              color: '#8e44ad',
              fontSize: 20,
            }}
          >
            {/* ConfigBox (empty) - future controls go here */}
          </Box>
        )}
        {/* Current Chart Date/Time and Animation Direction Buttons - Only visible when ConfigBox is hidden */}
        {!showConfig && (
          <>
            {/* Animation Direction Buttons */}
            <Box sx={{ position: 'absolute', top: 16, left: 0, width: '100%', display: 'flex', justifyContent: 'center', zIndex: 12 }}>
              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  sx={{ minWidth: 90, fontWeight: 'bold', backgroundColor: '#27ae60', '&:hover': { backgroundColor: '#219150' } }}
                  onClick={() => setAnimationDirection(-1)}
                >
                  Reverse
                </Button>
                <Box sx={{ width: 40 }} />
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  sx={{ minWidth: 90, fontWeight: 'bold', backgroundColor: '#27ae60', '&:hover': { backgroundColor: '#219150' } }}
                  onClick={() => setAnimationDirection(1)}
                >
                  Forward
                </Button>
              </Box>
            </Box>
            {/* Current Chart Date/Time - Top Center */}
            <Box sx={{ position: 'absolute', top: 48, left: 0, width: '100%', display: 'flex', justifyContent: 'center', zIndex: 10 }}>
              <TextField
                label="Current Chart Date/Time"
                value={currentChartDate ? (typeof currentChartDate.format === 'function' ? currentChartDate.format('YYYY-MM-DD HH:mm') : String(currentChartDate)) : ''}
                variant="outlined"
                size="small"
                InputProps={{ readOnly: true }}
                sx={{ minWidth: 260, background: '#f8f8f8', borderRadius: 1, textAlign: 'center' }}
              />
            </Box>
          </>
        )}
        {/* Chart Area */}
        <Box sx={{ flexGrow: 1, minHeight: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
          <ChartSVG 
              showBackground={showBackground}
              signTextLines={signTextLines}
              signTooltips={signTooltips}
            />
        </Box>
        {/* Bottom Button Row - Two-column layout */}
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', borderTop: '1px solid rgba(0,0,0,0.12)', p: 2, width: '100%' }}>
          {/* Left: CLOSE button */}
          <Box sx={{ flex: 'none', display: 'flex', justifyContent: 'flex-start' }}>
            <Button onClick={handleClose} sx={{ backgroundColor: 'grey.500', '&:hover': { backgroundColor: 'grey.700' }, color: 'white', minWidth: 80 }}>
              CLOSE
            </Button>
          </Box>
          {/* Center: Status message */}
          <TextField
            label="Status Message"
            value={statusText}
            variant="outlined"
            size="small"
            InputProps={{ readOnly: true }}
            sx={{ flexGrow: 1, mx: 2, background: '#f8f8f8' }}
          />
          {/* Right: Animation/date/rate/unit controls and animation buttons */}
          <Box sx={{ flex: 'none', display: 'flex', justifyContent: 'flex-end', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="From Date"
                value={fromDate}
                onChange={newValue => {
                  setFromDate(newValue);
                  if (newValue && newValue.isValid()) {
                    localStorage.setItem('visualUniverse_fromDate', newValue.format('YYYY-MM-DD'));
                  }
                  if (animationPhase !== 'idle') {
                    setAnimationPhase('idle');
                  }
                }}
                format="YYYY-MM-DD"
                slotProps={{ textField: { size: 'small', sx: { width: 155 }, variant: 'outlined' } }}
              />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="To Date"
                value={toDate}
                onChange={newValue => {
                  setToDate(newValue);
                  if (newValue && newValue.isValid()) {
                    localStorage.setItem('visualUniverse_toDate', newValue.format('YYYY-MM-DD'));
                  }
                  if (animationPhase !== 'idle') {
                    setAnimationPhase('idle');
                  }
                }}
                format="YYYY-MM-DD"
                minDate={fromDate}
                slotProps={{ textField: { size: 'small', sx: { width: 155 }, variant: 'outlined' } }}
              />
            </LocalizationProvider>
            <Typography variant="body2" sx={{ fontWeight: 'bold', ml: 2 }}>Rate of change (s):</Typography>
            <FormControl size="small">
              <Select value={rate} onChange={(e) => setRate(e.target.value)} sx={{ width: 60 }}>
                <MenuItem value={1}>1</MenuItem>
                <MenuItem value={2}>2</MenuItem>
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" sx={{ fontWeight: 'bold', ml: 2 }}>Change Unit:</Typography>
            <FormControl size="small">
              <Select value={unit} onChange={(e) => setUnit(e.target.value)} sx={{ width: 120 }}>
                <MenuItem value={2}>2 hours</MenuItem>
                <MenuItem value={8}>8 hours</MenuItem>
                <MenuItem value={16}>16 hours</MenuItem>
                <MenuItem value={24}>24 hours</MenuItem>
                <MenuItem value={168}>168 hours</MenuItem>
              </Select>
            </FormControl>
            <Button
              onClick={handleStart}
              disabled={!isStartEnabled || animationPhase !== 'idle'}
              sx={{ backgroundColor: 'primary.main', color: 'white', minWidth: 80, mx: 1 }}
            >
              START
            </Button>
            <Button
              onClick={handlePauseContinue}
              disabled={animationPhase === 'idle' || (animationPhase === 'paused' && !isStartEnabled)}
              sx={{ backgroundColor: animationPhase === 'paused' ? 'success.main' : 'warning.main', color: 'white', minWidth: 80, mr: 3 }}
            >
              {animationPhase === 'paused' ? 'CONTINUE' : 'PAUSE'}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

VisualUniverseDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  initialDate: PropTypes.string,
};

export default VisualUniverseDialog;
